import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Stage, Layer, Image as KonvaImage, Group, Rect, Line, Text } from 'react-konva';
import type Konva from 'konva';
import {
  centerNudge,
  clampToCover,
  coverTransform,
  exportMime,
  exportQuality,
  fitFontSize,
  useHtmlImage,
  type ExportFormat,
  type PhotoTransform,
} from './photoSlot';
import { STORY_FONT, type StoryTemplate, type StoryTextBox } from './stories';

const LINE_HEIGHT = 1.05;

export interface StoryShow {
  photoSrc: string | null;
  transform: PhotoTransform | null;
  /** Artist name (guest) or show name (resident). */
  name: string;
  /** Resident only: performing artist. */
  artist?: string;
  time: string;
}

export interface StoryValue {
  date: string;
  shows: StoryShow[];
}

export interface StoryEditorHandle {
  toDataURL: (format?: ExportFormat) => string | null;
  toBlob: (format?: ExportFormat) => Promise<Blob | null>;
}

interface StoryEditorProps {
  template: StoryTemplate;
  value: StoryValue;
  onChange: (next: StoryValue) => void;
  className?: string;
}

/** A photo clipped to a slot, draggable + wheel-zoom, always covering the frame. */
function PhotoSlotNode({
  src,
  frame,
  transform,
  onTransform,
}: {
  src: string | null;
  frame: { x: number; y: number; width: number; height: number };
  transform: PhotoTransform | null;
  onTransform: (t: PhotoTransform) => void;
}) {
  const img = useHtmlImage(src);
  const imgRef = useRef<Konva.Image>(null);

  useEffect(() => {
    if (img && !transform) onTransform(coverTransform(img.width, img.height, frame));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, frame.x, frame.y, frame.width, frame.height]);

  return (
    <Group clipX={frame.x} clipY={frame.y} clipWidth={frame.width} clipHeight={frame.height}>
      {img && transform ? (
        <KonvaImage
          ref={imgRef}
          image={img}
          x={transform.x}
          y={transform.y}
          scaleX={transform.scale}
          scaleY={transform.scale}
          draggable
          onDragEnd={(e) => {
            const clamped = clampToCover(e.target.x(), e.target.y(), img.width, img.height, transform.scale, frame);
            onTransform({ scale: transform.scale, ...clamped });
          }}
          // Keep the image covering the frame during the drag (convert from
          // absolute/scaled coords using the stage scale).
          dragBoundFunc={(pos) => {
            if (!transform) return pos;
            const s = imgRef.current?.getStage()?.scaleX() ?? 1;
            const local = clampToCover(pos.x / s, pos.y / s, img.width, img.height, transform.scale, frame);
            return { x: local.x * s, y: local.y * s };
          }}
        />
      ) : (
        <Rect x={frame.x} y={frame.y} width={frame.width} height={frame.height} fill="#ffffff" opacity={0.06} />
      )}
    </Group>
  );
}

export const StoryEditor = forwardRef<StoryEditorHandle, StoryEditorProps>(function StoryEditor(
  { template, value, onChange, className },
  ref,
) {
  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { canvas } = template;

  const backImg = useHtmlImage(template.assets.back);
  const logoImg = useHtmlImage(template.assets.logo);
  const [fontReady, setFontReady] = useState(false);
  const [fit, setFit] = useState(0.3);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const availW = el.clientWidth;
      const availH = Math.min(window.innerHeight * 0.72, 1100);
      setFit(Math.min(availW / canvas.width, availH / canvas.height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [canvas.width, canvas.height]);

  useEffect(() => {
    let active = true;
    const done = () => active && setFontReady(true);
    if (typeof document !== 'undefined' && 'fonts' in document) {
      Promise.all([
        document.fonts.load(`400 48px "${STORY_FONT}"`),
        document.fonts.load(`700 48px "${STORY_FONT}"`),
      ])
        .then(() => document.fonts.ready)
        .then(done)
        .catch(done);
    } else {
      done();
    }
    return () => {
      active = false;
    };
  }, []);

  const setShow = useCallback(
    (index: number, patch: Partial<StoryShow>) => {
      const shows = value.shows.map((s, i) => (i === index ? { ...s, ...patch } : s));
      onChange({ ...value, shows });
    },
    [value, onChange],
  );

  /**
   * Draw the stage at its native 1080x1920 — exactly Instagram's story size, so IG
   * uploads need no server-side resample (its resize pass re-encodes aggressively).
   */
  const renderAtNativeSize = useCallback(
    <T,>(draw: (stage: Konva.Stage) => T): T | null => {
      const stage = stageRef.current;
      if (!stage) return null;
      stage.scale({ x: 1, y: 1 });
      stage.size({ width: canvas.width, height: canvas.height });
      const out = draw(stage);
      stage.scale({ x: fit, y: fit });
      stage.size({ width: canvas.width * fit, height: canvas.height * fit });
      return out;
    },
    [fit, canvas.width, canvas.height],
  );

  useImperativeHandle(
    ref,
    () => ({
      toDataURL: (format: ExportFormat = 'png') =>
        renderAtNativeSize((s) => s.toDataURL({ pixelRatio: 1, mimeType: exportMime(format), quality: exportQuality(format) })),
      toBlob: (format: ExportFormat = 'png') =>
        new Promise<Blob | null>((resolve) => {
          const done = renderAtNativeSize((s) =>
            s.toCanvas({ pixelRatio: 1 }).toBlob((b: Blob | null) => resolve(b), exportMime(format), exportQuality(format)),
          );
          if (done === null) resolve(null);
        }),
    }),
    [renderAtNativeSize],
  );

  // Contain-fit the logo inside its box, top-left anchored.
  const logoDraw = useMemo(() => {
    const r = template.assets.logoRect;
    if (!logoImg) return r;
    const s = Math.min(r.width / logoImg.width, r.height / logoImg.height);
    return { x: r.x, y: r.y, width: logoImg.width * s, height: logoImg.height * s };
  }, [logoImg, template.assets.logoRect]);

  const shownCount = Math.min(value.shows.length, template.slots.length);

  /** Draw a text panel (navy/grey box + orange border + auto-fit Space Mono text). */
  const panel = (tb: StoryTextBox, text: string, keySeed: string) => {
    const ctx = document.createElement('canvas').getContext('2d');
    const size = ctx
      ? fitFontSize(ctx, text || ' ', tb.box, tb.fontSize, STORY_FONT, {
          lineHeight: LINE_HEIGHT,
          weight: tb.weight,
          min: 12,
        })
      : tb.fontSize;
    // Center by the glyph box (not the em box) so all-caps text sits centred.
    const nudge = ctx ? centerNudge(ctx, text || 'X', size, STORY_FONT, tb.weight) : 0;
    return (
      <Group key={keySeed}>
        <Rect
          x={tb.box.x}
          y={tb.box.y}
          width={tb.box.width}
          height={tb.box.height}
          fill={template.fill}
          stroke={template.stroke}
          strokeWidth={2}
          listening={false}
        />
        <Text
          key={fontReady ? `${keySeed}-r` : `${keySeed}-l`}
          x={tb.box.x}
          y={tb.box.y + nudge}
          width={tb.box.width}
          height={tb.box.height}
          text={text}
          fontFamily={STORY_FONT}
          fontSize={size}
          fontStyle={tb.weight >= 700 ? 'bold' : 'normal'}
          fill={template.textColor}
          align={tb.align}
          verticalAlign="middle"
          lineHeight={LINE_HEIGHT}
          wrap="word"
          listening={false}
        />
      </Group>
    );
  };

  const b = template.border;

  return (
    <div ref={containerRef} className={className}>
      <Stage
        ref={stageRef}
        width={canvas.width * fit}
        height={canvas.height * fit}
        scaleX={fit}
        scaleY={fit}
        style={{ borderRadius: 8, overflow: 'hidden' }}
      >
        <Layer>
          {/* Back plate */}
          {backImg && <KonvaImage image={backImg} width={canvas.width} height={canvas.height} listening={false} />}

          {/* Photo slots (drawn under the frame furniture) */}
          {value.shows.slice(0, shownCount).map((show, i) => (
            <PhotoSlotNode
              key={`photo-${i}`}
              src={show.photoSrc}
              frame={template.slots[i].photoFrame}
              transform={show.transform}
              onTransform={(t) => setShow(i, { transform: t })}
            />
          ))}

          {/* Per-slot photo borders (on top of the photo) */}
          {value.shows.slice(0, shownCount).map((_, i) => {
            const f = template.slots[i].photoFrame;
            return (
              <Rect
                key={`pb-${i}`}
                x={f.x + 1}
                y={f.y + 1}
                width={f.width - 2}
                height={f.height - 2}
                stroke={template.stroke}
                strokeWidth={2}
                listening={false}
              />
            );
          })}

          {/* Big outer frame (INSIDE stroke) */}
          <Rect
            x={b.rect.x + b.width / 2}
            y={b.rect.y + b.width / 2}
            width={b.rect.width - b.width}
            height={b.rect.height - b.width}
            stroke={template.stroke}
            strokeWidth={b.width}
            listening={false}
          />

          {/* Date + optional header */}
          {panel(template.date, value.date || 'DATE', 'date')}
          {template.header &&
            panel(
              { box: template.header.box, fontSize: template.header.fontSize, weight: template.header.weight, align: 'center' },
              template.header.text,
              'header',
            )}

          {/* Per-slot text: connectors, name, artist, time */}
          {value.shows.slice(0, shownCount).map((show, i) => {
            const slot = template.slots[i];
            return (
              <Group key={`txt-${i}`}>
                {slot.connectors?.map((c, j) => (
                  <Line
                    key={j}
                    points={[c.x, c.y, c.x, c.y + c.length]}
                    stroke={template.stroke}
                    strokeWidth={2}
                    listening={false}
                  />
                ))}
                {panel(slot.name, show.name || 'NAME', `name-${i}`)}
                {slot.artist && panel(slot.artist, show.artist || 'artist', `artist-${i}`)}
                {panel(slot.time, show.time || 'time', `time-${i}`)}
              </Group>
            );
          })}

          {/* Logo */}
          {logoImg && (
            <KonvaImage
              image={logoImg}
              x={logoDraw.x}
              y={logoDraw.y}
              width={logoDraw.width}
              height={logoDraw.height}
              listening={false}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
});
